import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Heart,
  Phone,
  AlertCircle,
} from "lucide-react";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import {
  PHQ9_ITEMS,
  PHQ9_OPTIONS,
  computePhq9,
  isCrisis,
  captureUtm,
  sessionId,
} from "./screenerData";
import { type Answers, type Step, getVisibleSteps } from "./screenerSteps";
import { ChoiceButtons } from "./ChoiceButtons";
import { Phq9Step } from "./Phq9Step";
import { ContactStep } from "./ContactStep";
import { CrisisInterstitial } from "./CrisisInterstitial";

const STORAGE_KEY = "saint_screener_state";

function pushDataLayer(event: string, step?: number) {
  if (typeof window === "undefined") return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event,
    step_number: step,
    session_id:
      (window as any).__screenerSid ||
      ((window as any).__screenerSid = sessionId()),
  });
}

const SaintScreener = () => {
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [showCrisis, setShowCrisis] = useState(false);

  // Contact fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [insurance, setInsurance] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Autosave restore ──
  useState(() => {
    if (typeof sessionStorage !== "undefined") {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const p = JSON.parse(saved);
          if (p.answers) setAnswers(p.answers);
          if (p.firstName) setFirstName(p.firstName);
          if (p.lastName) setLastName(p.lastName);
          if (p.email) setEmail(p.email);
          if (p.phone) setPhone(p.phone);
          if (p.insurance) setInsurance(p.insurance);
          if (p.consent) setConsent(p.consent);
          if (typeof p.stepIndex === "number") setStepIndex(p.stepIndex);
        } catch {}
      }
    }
    pushDataLayer("quiz_start");
  });

  // ── Autosave persist ──
  useState(() => {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          stepIndex,
          answers,
          firstName,
          lastName,
          email,
          phone,
          insurance,
          consent,
        }),
      );
    }
  });

  const visibleSteps = getVisibleSteps(answers);
  const currentStep = visibleSteps[stepIndex];
  const progress = Math.round((stepIndex / (visibleSteps.length - 1)) * 100);

  const goNext = () => {
    // Show crisis interstitial immediately when a safety-requiring answer
    // is detected — BEFORE any contact capture — and before showing the
    // on-screen result, so 988 is never behind a form.
    if (isCrisis(answers)) {
      setShowCrisis(true);
      return;
    }
    if (stepIndex < visibleSteps.length - 1) {
      const next = stepIndex + 1;
      setStepIndex(next);
      pushDataLayer("quiz_step", next);
    }
  };

  const goBack = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const setAnswer = (id: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const formatPhone = (raw: string) => {
    const d = raw.replace(/[^\d]/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const contactValid =
    firstName.trim() &&
    lastName.trim() &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    phone.replace(/[^\d]/g, "").length >= 10 &&
    consent;

  const submitLead = async () => {
    if (!contactValid) return;
    setSubmitting(true);
    pushDataLayer("lead_submitted");

    const utms = captureUtm();
    const payload = buildTrackingPayload(
      "saint-screener-multi-step",
      "SAINT Eligibility Screener",
    );
    payload.formData = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      ...utms,
    };
    payload.formLabels = {
      first_name: "First Name",
      last_name: "Last Name",
      email: "Email",
      phone: "Phone",
    };
    trackLead("saint-screener-multi-step", "SAINT Eligibility Screener");
    trackLeadConversion(
      "saint-screener-multi-step",
      "SAINT Eligibility Screener",
    );

    try {
      await postTrackingEvent(payload, {
        customFields: {
          "8SoVAfXkhwYBE1BkqHzH": {
            value: (answers["age"] as string) || "",
            label: "Age range",
          },
          uflMM55nFkJqxT1DFSwP: {
            value: (answers["depression_dx"] as string) || "",
            label: "Depression diagnosis",
          },
          h1crLgOEP39WuX2wS9RM: {
            value: (answers["current_episode"] as string) || "",
            label: "Currently in a depressive episode",
          },
          yVwMApocW0YrHqyAZ3vu: {
            value: (answers["meds_count"] as string) || "",
            label: "Number of antidepressants tried",
          },
          icB8IsBUv99d6d0hcHvt: {
            value: (answers["meds_30days"] as string) || "",
            label: "Took each medication for 30+ days",
          },
          Tawoueyv7TOhyjd6cQv1: {
            value: (answers["meds_stopped_reason"] as string) || "",
            label: "Why medications were stopped",
          },
          bzOvEofUsxNL20SMiwLD: {
            value: (answers["psychiatrist"] as string) || "",
            label: "Psychiatrist evaluation",
          },
          "76sVhbbNwwvIiCkhZFJ4": {
            value: (answers["talk_therapy"] as string) || "",
            label: "Tried talk therapy",
          },
          SdSRM58HSksuc08te5lT: {
            value: (answers["other_treatments"] as string) || "",
            label: "Other treatments tried",
          },
          Evr1AwYn67qB3QDfcQ0H: {
            value: String(computePhq9(answers)),
            label: "PHQ-9 score",
          },
          ...(insurance.trim()
            ? {
                qwYcMnzrsdvJCUTHCuwv: {
                  value: insurance.trim(),
                  label: "Insurance carrier (optional)",
                },
              }
            : {}),
        },
      });
    } catch {
      /* fire-and-forget — still redirect */
    }

    setSubmitting(false);

    // ── 988 crisis check AFTER form submission ──
    if (isCrisis(answers)) {
      setShowCrisis(true);
    } else {
      navigate("/thank-you-saint-google");
    }
  };

  // ── Crisis interstitial ──
  if (showCrisis) {
    return (
      <CrisisInterstitial
        onContinue={() => navigate("/thank-you-saint-google")}
      />
    );
  }

  // ── Render shared bits ──
  const seoProps = {
    title: "Am I a Candidate for SAINT TMS? | Free Eligibility Check",
    description:
      "Take our free SAINT TMS eligibility check. Share your contact info and our clinical team will reach out within one business day.",
    canonical: "/am-i-a-candidate-screener",
    keywords:
      "SAINT TMS candidate, am I a candidate for TMS, TMS eligibility, depression treatment screener",
  };

  // ── Intro ──
  if (currentStep?.type === "intro") {
    return (
      <>
        <SEOHead {...seoProps} />
        <Navbar />
        <main className="min-h-screen bg-gradient-to-b from-card to-background py-8 sm:py-12">
          <div className="container-px mx-auto max-w-xl">
            <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft sm:p-8">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/10">
                <Heart className="h-8 w-8 text-accent" />
              </div>
              <h1 className="font-headings text-2xl font-extrabold text-primary sm:text-3xl">
                Am I a candidate for SAINT TMS?
              </h1>
              <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
                SAINT is an FDA-cleared, 5-day accelerated treatment for
                depression, developed at Stanford. Answer a few quick questions
                to see if it may be right for you. Takes about 2 minutes. Your
                answers are private.
              </p>
              <button
                onClick={goNext}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-4 text-lg font-bold text-accent-foreground shadow-glow transition-opacity hover:opacity-90"
              >
                Check my eligibility
                <ArrowRight className="h-5 w-5" />
              </button>
              <div className="mt-5 flex items-start justify-center gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <p className="text-xs text-muted-foreground">
                  Your answers are private. This is not medical advice or a
                  diagnosis.
                </p>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Render current step content ──
  const renderStepContent = () => {
    if (!currentStep) return null;

    if (currentStep.type === "phq9") {
      return (
        <Phq9Step
          answers={answers}
          onAnswer={(val) => setAnswer("phq9", val)}
          onNext={goNext}
          onBack={goBack}
        />
      );
    }

    if (currentStep.type === "contact") {
      return (
        <ContactStep
          firstName={firstName}
          lastName={lastName}
          email={email}
          phone={phone}
          insurance={insurance}
          consent={consent}
          submitting={submitting}
          contactValid={!!contactValid}
          onFirstName={setFirstName}
          onLastName={setLastName}
          onEmail={setEmail}
          onPhone={(v) => setPhone(formatPhone(v))}
          onInsurance={setInsurance}
          onConsent={(v) => setConsent(v)}
          onSubmit={submitLead}
          helpText={currentStep.helpText}
        />
      );
    }

    return (
      <ChoiceButtons
        step={currentStep}
        answers={answers}
        onAnswer={setAnswer}
        onNext={goNext}
      />
    );
  };

  return (
    <>
      <SEOHead {...seoProps} />
      <Navbar />
      <main className="min-h-screen bg-gradient-to-b from-card to-background py-8 sm:py-12">
        <div className="container-px mx-auto max-w-xl">
          {/* Progress bar */}
          {stepIndex > 0 && (
            <div className="mb-6">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">
                  Step {stepIndex} of {visibleSteps.length - 1}
                </span>
                <span className="text-xs font-semibold text-muted-foreground">
                  {progress}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-accent transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Card */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
            {renderStepContent()}
          </div>

          {/* Back button */}
          {stepIndex > 0 && currentStep?.type !== "contact" && (
            <button
              onClick={goBack}
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default SaintScreener;
